import React from "react";
import { Plus, X, FileText, Pin } from "lucide-react";

/**
 * Excel-style tab bar for multi-asset workspace.
 * Each tab represents an independent workspace (symbol + timeframe + chart config).
 *
 * Props:
 *   sheets: [{id, symbol, timeframe, name?}]
 *   activeId: string
 *   onSelect: (id) => void
 *   onAdd: () => void
 *   onClose: (id) => void
 *   onRename: (id, newName) => void
 */
export default function SheetTabs({ sheets, activeId, onSelect, onAdd, onClose, onRename }) {
  const startRename = (id, current) => {
    const val = window.prompt("Nom de l'onglet :", current || "");
    if (val !== null && val.trim()) onRename?.(id, val.trim());
  };

  return (
    <div className="flex items-center gap-0.5 border-b border-white/5 bg-black/20 overflow-x-auto no-scrollbar" data-testid="sheet-tabs">
      {sheets.map((s) => {
        const active = s.id === activeId;
        return (
          <div
            key={s.id}
            role="tab"
            aria-selected={active}
            className={`group flex items-center gap-1.5 px-3 py-2 border-t-2 min-w-0 cursor-pointer transition-colors ${active ? "border-accent-blue bg-black/40" : "border-transparent hover:bg-white/5"}`}
            onClick={() => onSelect(s.id)}
            onDoubleClick={() => startRename(s.id, s.name || s.symbol)}
            data-testid={`sheet-tab-${s.id}`}
            title="Double-clic pour renommer"
          >
            <FileText size={11} className={active ? "text-accent-blue" : "text-white/50"} />
            <span className={`text-xs font-medium truncate max-w-[140px] ${active ? "text-white" : "text-white/70"}`}>
              {s.name || s.symbol}
            </span>
            <span className={`text-[9px] font-mono px-1 rounded ${active ? "bg-accent-blue/20 text-accent-blue" : "bg-white/5 text-white/40"}`}>
              {s.timeframe}
            </span>
            {sheets.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); onClose?.(s.id); }}
                className={`w-4 h-4 rounded flex items-center justify-center transition-opacity ${active ? "opacity-70 hover:opacity-100 hover:bg-red-500/20" : "opacity-0 group-hover:opacity-100 hover:bg-red-500/20"}`}
                data-testid={`sheet-close-${s.id}`}
                aria-label="Fermer"
              >
                <X size={10} />
              </button>
            )}
          </div>
        );
      })}
      <button
        onClick={onAdd}
        className="px-2.5 py-2 text-white/50 hover:text-white hover:bg-white/5 border-t-2 border-transparent"
        data-testid="sheet-add"
        title="Nouvelle feuille"
      >
        <Plus size={13} />
      </button>
    </div>
  );
}
